import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationStart, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { AuthService } from 'src/app/core/services/auth.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { SearchService } from 'src/app/shared/services/search.service';
import { RouteService } from 'src/app/core/services/route.service';
import { NavigationVisibilityService } from 'src/app/core/services/navigation-visibility.service';
import { LoadNavigation } from 'src/app/core/state/_actions/table.actions';
import { SubSink } from 'subsink';
import { LaunchDarklyService } from 'src/app/core/services/launchdarkly.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
})
export class HeaderComponent implements OnInit, AfterViewInit {
  public isHeaderHidden: boolean = false;
  public isAuthScreen: boolean = true;
  public isSearchVisible: boolean = false;
  public subscription: Subscription | null = null;
  public toggelEvent: boolean = false;
  public hideMainContent: boolean = false;
  public hideSidebar: boolean = false;
  public isNewSidebarEnabled: boolean = false;
  subsink: SubSink = new SubSink();

  constructor(
    private router: Router,
    private searchService: SearchService,
    private authService: AuthService,
    private storage: StorageService,
    private readonly cdr: ChangeDetectorRef,
    private readonly route: RouteService,
    private readonly store: Store,
    private readonly navVisibility: NavigationVisibilityService,
    private readonly launchDarklyService: LaunchDarklyService
  ) {}

  ngOnInit() {
    const initiallyHidden = this.shouldHideHeader();
    this.isAuthScreen = initiallyHidden;
    this.hideMainContent = initiallyHidden;
    this.hideSidebar = initiallyHidden;
    this.isHeaderHidden = initiallyHidden || this.router.url !== '/';

    // Dispatch LoadNavigation when sidebar is hidden to ensure navigation data loads
    const appKey = this.route.getApplicationKey();
    if (appKey && this.hideSidebar) {
      this.store.dispatch(new LoadNavigation(appKey));
    }

    this.subsink.add(
      this.launchDarklyService.getFlagValue(FEATURE_FLAGS.ENABLE_NEW_NAV_SIDEBAR, false)
        .subscribe((value) => {
          this.isNewSidebarEnabled = !!value;
        })
    );
  }

  ngAfterViewInit() {
    // Subscribe after view init to avoid NG0100 by changing bindings during first check
    this.routeEventListener();
  }

  /**
   * @returns full name
   */
  getFullName = () => {
    const name = `${this.authService.userProfile?.FirstName || ''} ${
      this.authService.userProfile?.LastName || ''
    }`;
    if (!name.replace(/\s/g, '')) return this.authService.userProfile?.Email;
    return name;
  };

  searchText(event: any) {
    this.searchService.onInputChange(event.target.value);
  }

  /**
   * Toggle event
   */
  toggleEvent($event: any) {
    this.toggelEvent = $event;
  }

  /**
   * Toggle search
   */
  toggleSearch(input: HTMLInputElement) {
    this.isSearchVisible = !this.isSearchVisible;
    if (!this.isSearchVisible) {
      this.searchService.onInputChange('');
    } else {
      input?.focus();
    }
  }

  logout() {
    this.authService.logout(true, false, this.storage.getPortalId());
  }

  /**
   * Router event listener
   */
  routeEventListener = () => {
    this.subsink.add(
    this.router.events.subscribe((event: any) => {
      const routeIndex = [
        '/auth/login',
        '/auth/forgot-password',
        '/auth/password-recovery',
        '/auth/hidden-5b67012b-4bc3-43ca-accd-89e563b5c6f7-login',
        '/auth/register',
        '/auth/terms',
        '/auth/privacy',
        'auth/contactBillingAdmin',
        'auth/resetpassword',
        'auth/resendmfacode',
        'auth/passwordrecovery',
        'verification',
        'settings/user-interface/pages/view'
      ].indexOf(this.router.url);
      // Defer state updates to next microtask to avoid ExpressionChangedAfterItHasBeenCheckedError
      Promise.resolve().then(() => {
        this.isHeaderHidden = this.shouldHideHeader() || this.router.url !== '/';
        if (event instanceof NavigationStart || event instanceof NavigationEnd) {
          const hide = this.shouldHideHeader(event);
          this.isAuthScreen = hide;
          this.hideMainContent = hide;
          this.hideSidebar = hide;

          // Dispatch LoadNavigation when sidebar is hidden to ensure navigation data loads
          const appKey = this.route.getApplicationKey();
          if (appKey && this.hideSidebar) {
            this.store.dispatch(new LoadNavigation(appKey));
          }
        }
        try { this.cdr.detectChanges(); } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('HeaderComponent: detectChanges() failed after router event', err);
        }
      });
    }));
  };

  /**
   * Should header be hidden
   * @return true if header to be hidden
   */
  shouldHideHeader = (event?:any) => {
    if (this.router.url.includes('file-view') || event?.url?.includes('file-view')) return true;
    const hiddenToRoutes = [
      '?xnav=1',
      '/auth/login',
      '/auth/forgot-password',
      '/auth/password-recovery',
      '/auth/hidden-5b67012b-4bc3-43ca-accd-89e563b5c6f7-login',
      '/auth/register',
      '/auth/terms',
      '/auth/privacy',
      'auth/contactBillingAdmin',
      'auth/resetpassword',
      'auth/resendmfacode',
      'auth/passwordrecovery',
      'verification',
    ];
    for (let route of hiddenToRoutes)
      if (this.router.url.includes(route) || event?.url?.includes(route)) return true;

    // Check if navigation should be hidden based on role setting
    if (this.navVisibility.shouldHideNavForRole()) return true;

    return false;
  };

  /**
  *
  * @returns navigate back the url
  */
  navigateBack = () => window.history.back();

  /**
  *
  * @returns refreshes the web page
  */
  refreshContent = () =>  window.location.reload();

  ngOnDestroy(){
    this.subsink.unsubscribe();
  }
}
